/*
 * biobio-pdp-complementares.js: Web Component <pdp-complementares>
 *
 * Bloco "Leve mais, pague menos" da PDP-mestre: ancora (o produto da PDP) +
 * ate 3 complementares (custom.upsell), com preco marginal derivado da escada
 * da campanha (custom.pdp_complementares) e add-to-cart de N line items numa
 * chamada so. Dirige o markup de snippets/biobio-pdp-complementares.liquid.
 * Spec: docs/specs/2026-09-11-pdp-complementares-design.md
 *
 * Parte 1: Calc, motor de preco PURO (sem DOM), testado em node
 * (.scratch/complementares/calc.test.cjs). Parte 2: o custom element.
 *
 * Regras de preco (spec secao 5), tudo em centavos inteiros:
 *   pct         = pct_n da campanha (n = 1 + marcados; n >= 3 usa pct_3), 0 se desligada
 *   linha       = price - Math.round(price * pct / 100)   (arredonda POR LINHA)
 *   marginalSeq = total(ancora + antes + item) - total(ancora + antes), onde
 *                 antes = os marcados ANTES do item na ordem de marcacao (item
 *                 marcado) ou TODOS os marcados (previa de item desmarcado).
 *                 Ancora + marginais dos marcados soma o total, por
 *                 telescopagem (decisao 7 da spec, revisada 12/09: era fixo
 *                 contra a ancora sozinha, e fixo nao fecha a soma).
 * O degrau de 2 fica 1 a 2 centavos abaixo do carrinho (que aplica 15% e depois
 * 10% em duas etapas). Aceito na spec; nao "corrigir" com percentual magico.
 *   modo combo: total = preco da variante do combo do conjunto marcado; marginal
 *               sequencial identico, so muda a fonte.
 */
(function (root) {
  'use strict';

  var Calc = {
    pctFor: function (pcts, n, ativa) {
      if (!ativa || !pcts) return 0;
      var chave = n >= 3 ? '3' : (n === 2 ? '2' : '1');
      var v = parseFloat(pcts[chave]);
      return isNaN(v) ? 0 : v;
    },
    linha: function (preco, pct) {
      return preco - Math.round(preco * pct / 100);
    },
    cesta: function (precos, pcts, ativa) {
      var pct = Calc.pctFor(pcts, precos.length, ativa);
      var lista = 0, total = 0;
      for (var i = 0; i < precos.length; i++) {
        lista += precos[i];
        total += Calc.linha(precos[i], pct);
      }
      return { n: precos.length, pct: pct, lista: lista, total: total, economia: Math.max(0, lista - total) };
    },
    // Marginal SEQUENCIAL (spec secao 5): quanto o item acrescenta a cesta que
    // ja existe. Serve aos dois casos: item marcado (antes = os marcados antes
    // dele) e previa de item desmarcado (antes = todos os marcados). A ancora
    // mais os marginais dos marcados somam o total, por telescopagem.
    marginalSeq: function (precoAncora, antes, precoItem, pcts, ativa) {
      var base = [precoAncora].concat(antes || []);
      return Calc.cesta(base.concat([precoItem]), pcts, ativa).total - Calc.cesta(base, pcts, ativa).total;
    },

    /* ---------- pricers (modo combo; spec 2026-09-13 secao 5) ----------
       Mesma interface para os dois modos: resolver(itens) -> {total, variante, lista}.
       itens = [{pid, preco, tom, temTom}], preco = LISTA em centavos, tom = titulo da
       variante escolhida ou null (ancora sem tom). O render usa so o pricer ativo. */
    somaLista: function (itens) {
      var s = 0;
      for (var i = 0; i < itens.length; i++) s += itens[i].preco || 0;
      return s;
    },
    pricerCampanha: function (pcts, ativa) {
      return {
        modo: 'campanha',
        resolver: function (itens) {
          var precos = itens.map(function (i) { return i.preco || 0; });
          return { total: Calc.cesta(precos, pcts, ativa).total, variante: null, lista: Calc.somaLista(itens) };
        }
      };
    },
    // Chave do conjunto = pids ordenados como STRING (mesma regra do Liquid, que
    // ordena strings): ids de 13 digitos ordenam igual numerica e lexicograficamente.
    chaveDe: function (pids) {
      return pids.map(String).sort().join('-');
    },
    pricerCombo: function (combos) {
      var porChave = {};
      (combos || []).forEach(function (c) {
        if (c && c.componentes && c.componentes.length) porChave[Calc.chaveDe(c.componentes)] = c;
      });
      return {
        modo: 'combo',
        resolver: function (itens) {
          var lista = Calc.somaLista(itens);
          if (itens.length === 1) return { total: itens[0].preco || 0, variante: null, lista: lista };
          var combo = porChave[Calc.chaveDe(itens.map(function (i) { return i.pid; }))];
          if (!combo) return { total: null, variante: null, lista: lista };
          // Casamento por titulo: toda entrada de v.tons tem de bater com o tom do
          // item daquele pid. Item COM tom que o combo nao mapeia = descasamento.
          // Item sem tom (variante unica) so participa se o combo o mapear.
          var candidatas = (combo.variantes || []).filter(function (v) {
            var tons = v.tons || {};
            return itens.every(function (i) {
              var t = tons[String(i.pid)];
              if (t === undefined) return !i.temTom;
              return i.tom == null || String(t).trim() === String(i.tom).trim();
            });
          });
          if (!candidatas.length) return { total: null, variante: null, lista: lista };
          var completo = itens.every(function (i) { return !i.temTom || i.tom != null; });
          if (completo) return { total: candidatas[0].preco, variante: candidatas[0], lista: lista };
          // Sem algum tom: teto entre as candidatas disponiveis (mesma regra do precoTeto).
          var teto = null;
          candidatas.forEach(function (v) { if (v.disponivel && (teto === null || v.preco > teto)) teto = v.preco; });
          if (teto === null) teto = candidatas[0].preco;
          return { total: teto, variante: null, lista: lista };
        }
      };
    },
    cestaP: function (pricer, itens) {
      var r = pricer.resolver(itens);
      if (r.total == null) return null;
      return { n: itens.length, lista: r.lista, total: r.total, economia: Math.max(0, r.lista - r.total), variante: r.variante };
    },
    // Marginal sequencial sobre o pricer ativo. null se algum lado nao resolve.
    marginalSeqP: function (pricer, ancora, antes, item) {
      var base = [ancora].concat(antes || []);
      var com = pricer.resolver(base.concat([item])).total;
      var sem = pricer.resolver(base).total;
      if (com == null || sem == null) return null;
      return com - sem;
    }
  };

  // Em node (teste) o arquivo e um modulo: exporta e para aqui. No navegador
  // segue para a parte 2 (Task 3 acrescenta o componente abaixo desta linha).
  if (typeof module !== 'undefined' && module.exports) { module.exports = Calc; return; }
  root.BioBioPdpComplementaresCalc = Calc;

  var BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
  var money = function (cents) { return BRL.format((cents || 0) / 100); };
  // "por mais {{ valor }}" -> "por mais R$ 57,53". Placeholders no padrao das locales.
  var fmt = function (tpl, vars) {
    return String(tpl || '').replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, k) { return vars[k] != null ? vars[k] : ''; });
  };

  /* ---------- instrumentacao do funil ----------
     Clarity e Hotjar ja rodam na JANELA PRINCIPAL do tema (nao em web pixel),
     entao o bloco fala com eles direto. O publish na analytics da Shopify e
     pro caso de um pixel custom (GA4) passar a escutar depois, sem precisar
     mexer aqui de novo. Os dois snippets criam uma fila sincrona, entao chamar
     antes do script carregar e seguro.
     NUNCA lanca: medicao quebrada nao pode derrubar o caminho de compra. */
  var medido = {};
  var medir = function (etapa, payload, unico) {
    if (unico) {
      if (medido[etapa]) return;
      medido[etapa] = true;
    }
    var nome = 'pdp_comp_' + etapa;
    try {
      if (typeof root.clarity === 'function') {
        root.clarity('set', 'pdp_complementares', etapa);
        root.clarity('event', nome);
      }
    } catch (e) {}
    try { if (typeof root.hj === 'function') root.hj('event', nome); } catch (e) {}
    try {
      var sa = root.Shopify && root.Shopify.analytics;
      if (sa && typeof sa.publish === 'function') sa.publish(nome, payload || {});
    } catch (e) {}
  };
  root.BioBioPdpComplementaresMedir = medir;

  if (!root.customElements || root.customElements.get('pdp-complementares')) return;

  class PdpComplementares extends HTMLElement {
    connectedCallback() {
      if (this._inited) return;
      this._inited = true;
      this.abort = new AbortController();
      var signal = this.abort.signal;

      // designMode antes do primeiro parseJSON: e o proprio JSON de config (com
      // acento e chaves) que tem mais chance de vir quebrado do Liquid.
      this.designMode = this.dataset.designMode === 'true';
      this.cfg = this.parseJSON(this.querySelector(':scope > script[data-pdp-comp]'), null);
      // Config invalido: mesma degradacao do outro caminho sem o que vender,
      // nunca um CTA morto na pagina (Fix round 1, item C).
      if (!this.cfg) { this.hidden = true; return; }
      this.pct = this.cfg.pct || {};
      this.str = this.cfg.strings || {};
      // Modo (spec 2026-09-13, 4.2 e 9): combo so com combos; "lista" explicito
      // fica lista; qualquer outra coisa (Correcao 3, achada ao rodar o Step 2:
      // INCLUSIVE ausente) = campanha. Os 4 harnesses anteriores a este recurso
      // (harness.html e os 3 -sem-*/ancora-sem-tom) nao tem a chave `modo` e
      // dependem de `ativa`/`pct` pra continuar descontando como hoje; um
      // default pra "lista" force-desligaria o desconto deles (forcaria
      // this.ativa a false), quebrando o contrato de regressao do Step 7. O
      // Liquid real (Task 3) sempre manda `modo` explicito, entao esse default
      // so protege fixture legado, nunca uma pagina de verdade.
      // `ativa` (escada de percentuais) so vale em modo campanha.
      this.modo = this.cfg.modo === 'combo' ? 'combo' : (this.cfg.modo === 'lista' ? 'lista' : 'campanha');
      if (this.modo === 'combo' && !(Array.isArray(this.cfg.combos) && this.cfg.combos.length)) this.modo = 'lista';
      this.ativa = this.modo === 'campanha' && this.cfg.ativa === true;
      this.pricer = this.modo === 'combo' ? Calc.pricerCombo(this.cfg.combos) : Calc.pricerCampanha(this.pct, this.ativa);
      this.comboVariante = null;
      this.setAttribute('data-modo', this.modo);

      // Uma entrada por linha; o JSON de variantes vem do snippet-item (SSR).
      this.rows = [];
      this.querySelectorAll('.pdp-comp__row').forEach(function (rowEl) {
        var data = this.parseJSON(rowEl.querySelector('script[data-row-json]'), null);
        if (!data) return;
        var byId = {};
        (data.variantes || []).forEach(function (v) { byId[v.id] = v; });
        this.rows.push({ el: rowEl, data: data, byId: byId, sel: data.inicial, isAnchor: rowEl.classList.contains('is-anchor') });
      }, this);
      this.anchor = this.rows.filter(function (r) { return r.isAnchor; })[0] || null;
      this.comps = this.rows.filter(function (r) { return !r.isAnchor; });
      // Sem ancora ou sem complementar compravel o bloco nao tem o que vender.
      if (!this.anchor || !this.comps.length) { this.hidden = true; return; }

      this.cta = this.querySelector('[data-cta]');
      this.errorEl = this.querySelector('[data-error]');
      this.comboEl = this.querySelector('[data-combo]');
      this.totalEl = this.querySelector('[data-total]');
      this.totalListEl = this.querySelector('[data-total-list]');
      this.econEl = this.querySelector('[data-econ]');
      this.marcados = [];

      this.bindSwatches(signal);
      this.bindChecks(signal);
      this.bindDetail(signal);
      if (this.cta) this.cta.addEventListener('click', this.handleAdd.bind(this), { signal: signal });
      // Mesmo sinal na troca de degrau: o evento leva `selected` (Task 3 do plano pdp-v2-base).
      var onOffer = this.onOfferVariant.bind(this);
      document.addEventListener('pdp-offer:variant-changed', onOffer, { signal: signal });
      document.addEventListener('pdp-offer:tier-changed', onOffer, { signal: signal });

      // Nenhum complementar veio marcado (o 1o candidato do upsell nao rendeu linha,
      // porque so tinha variante oculta/esgotada): marca o primeiro que rendeu.
      if (!this.comps.some(this.isChecked, this)) {
        var c0 = this.comps[0].el.querySelector('input[data-row-check]');
        if (c0) c0.checked = true;
      }

      // Ordem de marcacao (spec secao 5): quem ja nasce marcado, na ordem em
      // que aparece no DOM. Marcar so acrescenta ao fim (onCheck); desmarcar
      // remove. Decide o `antes` do marginal sequencial de cada linha.
      this.ordem = this.comps.filter(this.isChecked, this);

      // Tom inicial da ancora = estado da OFERTA (decisao do Breno 11/09): adota o
      // swatch ja marcado la; sem selecao la, nasce sem tom e o CTA trava ate alguem
      // escolher, em qualquer dos dois lados. Sem <pdp-offer>, o bloco e a fonte e
      // usa a variante preferida do SSR (data.inicial).
      var offer = document.querySelector('pdp-offer');
      if (this.anchor.data.temTom) {
        this._syncing = true;
        try {
          if (offer) {
            var selEl = this.offerSelectedSwatch(offer);
            var selId = selEl ? Number(selEl.dataset.variantId) : null;
            if (selId && this.anchor.byId[selId]) this.applyVariant(this.anchor, selId);
            else this.clearAnchor();
          } else {
            this.applyVariant(this.anchor, this.anchor.data.inicial);
          }
        } finally { this._syncing = false; }
      }

      this.render();
      this.observarVisao(signal);
    }

    disconnectedCallback() { if (this.abort) this.abort.abort(); }

    /* ---------- funil: viu / interagiu / adicionou ---------- */

    // "viu" = parte relevante do bloco na tela por 1s seguido. Sem o tempo de
    // permanencia, quem rola rapido ate o rodape contaria como quem leu.
    // O bloco pode ser mais alto que a tela (5 PDPs medidas entre 469 e 703px,
    // mas o estoque muda isso): nesse caso 50% nunca entra, entao o alvo cai
    // pro que caberia em 60% da tela.
    observarVisao(signal) {
      if (typeof root.IntersectionObserver !== 'function') return;
      var alvo = 0.5;
      var alturaBloco = this.getBoundingClientRect().height || 0;
      var alturaTela = root.innerHeight || 0;
      if (alturaTela > 0 && alturaBloco > alturaTela) {
        alvo = Math.max(0.15, (alturaTela * 0.6) / alturaBloco);
      }
      var self = this;
      var timer = null;
      var io = new root.IntersectionObserver(function (entradas) {
        entradas.forEach(function (e) {
          if (e.isIntersecting) {
            if (timer) return;
            timer = root.setTimeout(function () {
              timer = null;
              medir('viu', { ancora: self.anchor.data.id, complementares: self.comps.length, modo: self.modo }, true);
              io.disconnect();
            }, 1000);
          } else if (timer) {
            root.clearTimeout(timer);
            timer = null;
          }
        });
      }, { threshold: alvo });
      io.observe(this);
      if (signal) {
        signal.addEventListener('abort', function () {
          if (timer) root.clearTimeout(timer);
          io.disconnect();
        });
      }
    }

    // Primeiro gesto da cliente dentro do bloco. O guard de _syncing e o que
    // impede a sincronia com a oferta de contar como interacao humana.
    marcarInteracao(gesto) {
      if (this._syncing) return;
      medir('interagiu', { ancora: this.anchor.data.id, gesto: gesto, modo: this.modo }, true);
    }

    parseJSON(el, fallback) {
      if (!el) return fallback;
      try {
        var parsed = JSON.parse(el.textContent);
        return parsed == null ? fallback : parsed;
      } catch (err) {
        if (this.designMode) console.warn('[pdp-complementares] JSON invalido', err);
        return fallback;
      }
    }

    rowOf(node) {
      var el = node.closest('.pdp-comp__row');
      for (var i = 0; i < this.rows.length; i++) if (this.rows[i].el === el) return this.rows[i];
      return null;
    }

    /* ---------- tom (swatches) ---------- */
    // Mesmo padrao da oferta: clique no LABEL (confiavel) + change do radio
    // (teclado). applyVariant ignora re-selecao, entao disparar 2x nao duplica.
    bindSwatches(signal) {
      this.querySelectorAll('.pdp-comp__swatches .swatch').forEach(function (sw) {
        var input = sw.querySelector('input[type="radio"]');
        if (!input) return;
        sw.addEventListener('click', function () {
          if (input.disabled) return;
          if (!input.checked) input.checked = true;
          this.onSwatch(sw);
        }.bind(this), { signal: signal });
        input.addEventListener('change', this.onSwatch.bind(this, sw), { signal: signal });
      }, this);
    }

    onSwatch(sw) {
      if (sw.classList.contains('swatch--disabled') || sw.classList.contains('swatch--selected')) return;
      this.marcarInteracao('escolheu-tom');
      var row = this.rowOf(sw);
      var id = Number(sw.dataset.variantId);
      if (!row || !row.byId[id]) return;
      this.applyVariant(row, id);
      // Controle remoto (decisao 8): so a ancora propaga, e nunca durante um eco.
      if (row.isAnchor && !this._syncing) this.pushToOffer(id);
    }

    // Marca o swatch, troca tom/imagem/link da linha e recalcula. NAO propaga
    // para fora: quem chamou decide (guard de reentrancia em _syncing).
    applyVariant(row, id) {
      var v = row.byId[id];
      if (!v) return;
      row.sel = id;
      row.el.querySelectorAll('.pdp-comp__swatches .swatch').forEach(function (s) {
        var on = Number(s.dataset.variantId) === id;
        s.classList.toggle('swatch--selected', on);
        var r = s.querySelector('input[type="radio"]');
        if (r && on && !r.checked) r.checked = true;
      });
      var tomEl = row.el.querySelector('[data-row-tom]');
      if (tomEl) tomEl.textContent = v.tom || '';
      var img = row.el.querySelector('[data-row-img]');
      if (img && v.imagem && img.getAttribute('src') !== v.imagem) img.setAttribute('src', v.imagem);
      var link = row.el.querySelector('[data-pdp-link]');
      if (link && row.data.temTom && row.data.url) link.setAttribute('href', row.data.url + '?variant=' + id);
      this.render();
    }

    /* ---------- sincronia com <pdp-offer> (decisao 8) ---------- */
    // Bloco -> oferta: clica no LABEL do swatch da oferta (o que a cliente
    // clicaria; o form do tema engole 'change' programatico). Modo cards: no
    // tier marcado, 1a unidade. Modo direto: o unico grupo. A oferta reemite
    // pdp-offer:variant-changed, que onOfferVariant ignora enquanto _syncing.
    pushToOffer(id) {
      var offer = document.querySelector('pdp-offer');
      if (!offer) { this.writeUrlVariant(id); return; }
      var scope = null;
      try { scope = offer.querySelector('.pdp-offer__tier:has(> input:checked) .pdp-offer__unit'); } catch (e) { scope = null; }
      var target = (scope || offer).querySelector('label.swatch[data-variant-id="' + id + '"]');
      if (!target || target.classList.contains('swatch--selected') || target.classList.contains('swatch--disabled')) return;
      this._syncing = true;
      try { target.click(); } finally { this._syncing = false; }
    }

    // Oferta -> bloco. Em modo cards o evento sai de QUALQUER unidade; a
    // ancora e UMA Base, entao segue so a 1a unidade (units[0]). Sem units[0]
    // escolhida (a cliente mexeu na 2a antes da 1a), nao move a ancora.
    onOfferVariant(e) {
      if (this._syncing || !this.anchor) return;
      // Variante unica: a ancora nao tem tom, nada aqui pra sincronizar.
      if (!this.anchor.data.temTom) return;
      var d = (e && e.detail) || {};
      var sel = d.selected || {};
      var id = d.variantId;
      if (sel.units) {
        if (!sel.units[0]) return;
        id = sel.units[0];
      } else if (sel.direct) {
        id = sel.direct;
      }
      id = Number(id);
      if (this.anchor.sel === id) return; // ja sincronizado: segura o eco assincrono, nao mexer
      if (!this.anchor.byId[id]) {
        // Tom que o bloco nao conhece (filtros divergentes entre os snippets):
        // trava em vez de manter o tom antigo escondido com o CTA liberado (decisao 13).
        this._syncing = true;
        try { this.clearAnchor(); this.render(); } finally { this._syncing = false; }
        return;
      }
      this._syncing = true;
      try { this.applyVariant(this.anchor, id); } finally { this._syncing = false; }
    }

    // Swatch marcado na oferta: 1a unidade do tier marcado (cards) ou o grupo unico (direto).
    offerSelectedSwatch(offer) {
      var el = null;
      try { el = offer.querySelector('.pdp-offer__tier:has(> input:checked) .pdp-offer__unit .swatch--selected'); } catch (e) { el = null; }
      return el || offer.querySelector('.pdp-offer__swatches .swatch--selected');
    }

    // Ancora sem tom: nenhum swatch marcado, texto 'escolha o tom'. O render() trava o CTA.
    clearAnchor() {
      var a = this.anchor;
      a.sel = null;
      a.el.querySelectorAll('.pdp-comp__swatches .swatch').forEach(function (s) {
        s.classList.remove('swatch--selected');
        var r = s.querySelector('input[type="radio"]');
        if (r) r.checked = false;
      });
      var tomEl = a.el.querySelector('[data-row-tom]');
      if (tomEl) tomEl.textContent = this.str.ancora_escolha || this.str.escolha_tom || 'escolha o tom';
    }

    // So sem <pdp-offer> na pagina: o bloco vira a fonte do tom e grava a URL.
    writeUrlVariant(id) {
      try {
        var u = new URL(root.location.href);
        u.searchParams.set('variant', String(id));
        root.history.replaceState(null, '', u.pathname + u.search);
      } catch (e) { /* sem URL API, a URL fica como esta */ }
    }

    /* ---------- marcar / desmarcar ---------- */
    bindChecks(signal) {
      this.querySelectorAll('input[data-row-check]').forEach(function (chk) {
        chk.addEventListener('change', this.onCheck.bind(this, chk), { signal: signal });
      }, this);
    }
    // Mantem a ordem de marcacao (spec secao 5): marcar acrescenta ao FIM (se
    // ainda nao estiver), desmarcar remove. Marcar nunca reordena quem ja
    // estava, por isso o marginal de quem ja estava marcado nao muda.
    onCheck(chk) {
      this.marcarInteracao('marcou');
      var row = this.rowOf(chk);
      if (row) {
        var idx = this.ordem.indexOf(row);
        if (chk.checked) {
          if (idx === -1) this.ordem.push(row);
        } else if (idx !== -1) {
          this.ordem.splice(idx, 1);
        }
      }
      this.render();
    }
    isChecked(row) {
      var c = row.el.querySelector('input[data-row-check]');
      return !!(c && c.checked);
    }

    /* ---------- detalhe na propria linha (decisao 12) ---------- */
    bindDetail(signal) {
      this.querySelectorAll('[data-more]').forEach(function (btn) {
        btn.addEventListener('click', function () { this.toggleDetail(btn, true); }.bind(this), { signal: signal });
      }, this);
      // Imagem e nome sao ATALHOS de toque para o botao da propria linha, nao
      // controles: sem botao (linha sem beneficios) ficam inertes.
      this.querySelectorAll('[data-toggle]').forEach(function (el) {
        el.addEventListener('click', function () {
          var row = el.closest('.pdp-comp__row');
          var btn = row ? row.querySelector('[data-more]') : null;
          if (btn) this.toggleDetail(btn, false);
        }.bind(this), { signal: signal });
      }, this);
    }
    toggleDetail(btn, viaBotao) {
      var det = document.getElementById(btn.getAttribute('aria-controls') || '');
      if (!det) return;
      this.marcarInteracao('expandiu');
      var abrir = btn.getAttribute('aria-expanded') !== 'true';
      btn.setAttribute('aria-expanded', abrir ? 'true' : 'false');
      det.hidden = !abrir;
      var txt = btn.querySelector('[data-more-text]');
      if (txt) txt.textContent = abrir ? (this.str.ver_menos || 'Ver menos') : (this.str.ver_mais || 'Ver mais');
      // Recolheu pela imagem/nome: o foco vai ao controle acessivel da linha.
      if (!abrir && !viaBotao) btn.focus({ preventScroll: true });
    }

    /* ---------- derivacao (Calc) e resumo ---------- */
    nome(row) {
      var v = row.byId[row.sel] || {};
      return row.data.apelido + (row.data.temTom && v.tom ? ' ' + v.tom : '');
    }
    setText(el, txt) { if (el) el.textContent = txt; }
    precoOf(row) {
      var v = row.byId[row.sel] || {};
      return v.preco || 0;
    }

    // Item do pricer (spec 2026-09-13, secao 5): lista, tom escolhido (ou null) e se tem tom.
    itemOf(row) {
      var v = row.sel ? (row.byId[row.sel] || null) : null;
      return {
        pid: row.data.id,
        preco: v ? (v.preco || 0) : this.precoTeto(row),
        tom: v && v.tom ? v.tom : null,
        temTom: !!row.data.temTom
      };
    }

    // Sem tom escolhido, o preco do PRODUTO inteiro e o teto (maior preco entre
    // as variantes disponiveis), nunca uma variante arbitraria: mesma regra do
    // "product.price e o menor preco" do CLAUDE.md, aplicada ao inverso (Fix
    // round 1, item B). Sem nenhuma disponivel, ultimo recurso e a inicial.
    precoTeto(row) {
      var max = null;
      (row.data.variantes || []).forEach(function (v) {
        if (v.disponivel && (max === null || v.preco > max)) max = v.preco;
      });
      if (max !== null) return max;
      var ini = row.byId[row.data.inicial] || {};
      return ini.preco || 0;
    }

    render() {
      var a = this.anchor;
      // Com tom, o preco da variante escolhida. Sem tom, o teto (item B acima).
      var av = a.sel ? (a.byId[a.sel] || {}) : null;
      var precoA = av ? (av.preco || 0) : this.precoTeto(a);
      var pricer = this.pricer;
      var itemA = this.itemOf(a);
      var marcados = [];

      // Ancora: preco de 1 item (pct_1 em campanha; lista nos outros modos).
      this.setText(a.el.querySelector('[data-row-list]'), money(Calc.linha(precoA, Calc.pctFor(this.pct, 1, this.ativa))));

      // Ordem de marcacao decide o `antes` de cada linha (spec secao 5):
      // marcado usa os marcados ANTES dele na ordem; desmarcado usa TODOS os
      // marcados (previa de "e se eu marcar agora"). So essa previa fecha a
      // soma telescopica quando o item for de fato marcado.
      var marcadosEmOrdem = this.ordem.filter(this.isChecked, this);
      var itensMarcados = marcadosEmOrdem.map(this.itemOf, this);

      // A cesta final e conhecida ANTES de desenhar as linhas: os marcados saem
      // de marcadosEmOrdem, nao do laco. Precisa ser antes porque a linha so
      // sabe se mostra o marginal depois de saber se a combinacao esta
      // disponivel (spec secao 9: indisponivel = marginais escondidos).
      var itensFinais = [itemA].concat(itensMarcados);
      var cesta = Calc.cestaP(pricer, itensFinais);
      var somaLista = Calc.somaLista(itensFinais);
      this.comboVariante = cesta && cesta.variante ? cesta.variante : null;
      // Sem tom escolhido o CTA ja trava por semTom; isso nao e indisponibilidade.
      var semTomAlgum = itensFinais.some(function (i) { return i.temTom && i.tom == null; });
      this.indisponivel = this.modo === 'combo' && itensMarcados.length > 0 && !semTomAlgum &&
        (!cesta || !this.comboVariante || this.comboVariante.disponivel === false);

      this.comps.forEach(function (row) {
        var item = this.itemOf(row);
        var preco = item.preco;
        var checked = this.isChecked(row);
        var idx = marcadosEmOrdem.indexOf(row);
        // Blindagem: um marcado que por algum motivo nao esteja em marcadosEmOrdem
        // (idx -1, hoje inalcancavel) cai para TODOS os marcados em vez de
        // slice(0,-1), que cortaria o ultimo marcado em silencio.
        var antes = checked ? (idx < 0 ? itensMarcados : itensMarcados.slice(0, idx)) : itensMarcados;
        var marg = Calc.marginalSeqP(pricer, itemA, antes, item);
        var margEl = row.el.querySelector('[data-row-marg]');
        var listEl = row.el.querySelector('[data-row-list]');
        // Gate do "por mais": marginal resolvido e menor que a lista, em QUALQUER
        // modo (antes dependia de `ativa`, que esconderia o modo combo), e nunca
        // quando a combinacao esta indisponivel (spec secao 9).
        if (!this.indisponivel && marg != null && marg < preco) {
          this.setText(row.el.querySelector('[data-row-marg-val]'), fmt(this.str.por_mais, { valor: money(marg) }));
          this.setText(row.el.querySelector('[data-row-marg-list]'), money(preco));
          if (margEl) margEl.hidden = false;
          if (listEl) listEl.hidden = true;
        } else {
          this.setText(listEl, money(preco));
          if (listEl) listEl.hidden = false;
          if (margEl) margEl.hidden = true;
        }
        if (checked) { marcados.push(row); }
      }, this);

      this.setText(this.comboEl, [this.nome(a)].concat(marcados.map(this.nome, this)).join(' + '));
      this.setText(this.totalEl, money(cesta ? cesta.total : somaLista));
      var economia = cesta ? cesta.economia : 0;
      if (this.totalListEl) {
        this.totalListEl.hidden = economia <= 0;
        this.totalListEl.textContent = economia > 0 ? money(somaLista) : '';
      }
      if (this.econEl) {
        this.econEl.hidden = economia <= 0;
        this.econEl.textContent = economia > 0 ? fmt(this.str.economiza, { valor: money(economia) }) : '';
      }
      this.marcados = marcados;

      // Zero marcados: botao fica (sem saltar o layout), desabilitado.
      if (this.cta && !this.adding) {
        var apelidos = [a.data.apelido].concat(marcados.map(function (r) { return r.data.apelido; }));
        var semTom = !!(a.data.temTom && !a.sel);
        this.cta.disabled = semTom || marcados.length === 0 || this.indisponivel;
        this.cta.textContent = semTom
          ? (this.str.ancora_sem_variante || this.str.cta_sem_tom || 'Escolha o tom')
          : this.indisponivel
            ? (this.str.indisponivel || 'Combinação indisponível no momento')
            : (marcados.length ? fmt(this.str.cta, { combinacao: apelidos.join(' + ') }) : (this.str.cta_vazio || ''));
      }
    }

    /* ---------- add-to-cart (criterio duro: /cart/add.js, nunca Yampi) ---------- */
    buildItems() {
      var props = { _origem: 'pdp-complementares', _modo: this.modo };
      if (this.modo === 'combo') {
        // Uma linha so: a variante do combo casada no ultimo render (spec secao 7).
        if (!this.comboVariante || this.comboVariante.disponivel === false) return [];
        return [{ id: this.comboVariante.id, quantity: 1, properties: props }];
      }
      var items = [{ id: this.anchor.sel, quantity: 1, properties: props }];
      this.marcados.forEach(function (r) {
        items.push({ id: r.sel, quantity: 1, properties: props });
      });
      return items;
    }

    handleAdd(event) {
      if (event) event.preventDefault();
      if (!this.cta || this.cta.disabled || this.adding) return;
      if (!this.anchor.sel) return; // nunca adiciona tom nao escolhido, com ou sem temTom (Fix round 1, item D)
      var items = this.buildItems();
      if (!items.length || !this.marcados.length) return;
      var self = this;
      this.adding = true;
      this.cta.disabled = true;
      this.cta.setAttribute('aria-busy', 'true');
      this.cta.textContent = this.str.adicionando || 'Adicionando...';
      if (this.errorEl) { this.errorEl.hidden = true; this.errorEl.textContent = ''; }

      // Uma chamada, N line items: a Shopify e atomica (um item esgotado = nada entra).
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      })
        .then(function (res) {
          if (!res.ok) return res.json().catch(function () { return {}; }).then(function (d) {
            throw new Error(d.description || d.message || ('HTTP ' + res.status));
          });
          return res.json();
        })
        .then(function () {
          self.dispatchEvent(new CustomEvent('pdp-complementares:added', {
            bubbles: true, detail: { variantIds: items.map(function (i) { return i.id; }) },
          }));
          // Sem `unico`: duas adicoes na mesma sessao sao dois eventos.
          medir('adicionou', { ancora: self.anchor.data.id, itens: items.length, modo: self.modo });
          // Itens JA no carrinho: abrir o drawer. Se falhar, /cart. NUNCA o Yampi.
          var t = root.theme;
          var cartPage = !!(t && t.settings && t.settings.cartType === 'page');
          if (t && typeof t.updateCartSummaries === 'function' && !cartPage) {
            try { t.updateCartSummaries(true, true, false); }
            catch (e) { root.location.href = '/cart'; }
          } else {
            root.location.href = '/cart';
          }
        }, function (err) {
          // onError no segundo argumento do then, nunca um .catch encadeado: um
          // throw dentro do proprio sucesso acima nao pode cair aqui e mostrar
          // erro pra um add que deu certo (Fix round 1, item E).
          console.error('[pdp-complementares] add-to-cart falhou', err);
          if (self.errorEl) {
            self.errorEl.textContent = self.str.erro || 'Não foi possível adicionar. Tente de novo.';
            self.errorEl.hidden = false;
          }
        })
        .finally(function () {
          // .finally, nao .then: se o handler de SUCESSO acima lancar, um .then
          // de limpeza seria pulado e o CTA ficaria preso em "Adicionando..."
          // desabilitado, sem saida a nao ser recarregar a pagina.
          self.adding = false;
          if (self.cta) self.cta.removeAttribute('aria-busy');
          self.render(); // restaura rotulo e disabled conforme os marcados
        })
        .catch(function (err) {
          // .finally repassa a rejeicao adiante. So um throw do handler de
          // sucesso chega aqui (o de erro ja foi tratado): registra e para,
          // sem acusar falha num add que deu certo (mesma razao do item E).
          console.error('[pdp-complementares] pos-add', err);
        });
    }
  }

  root.customElements.define('pdp-complementares', PdpComplementares);
})(typeof window !== 'undefined' ? window : globalThis);
